import React, { useCallback, useEffect, useMemo, useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import services from '../../services'
import { useNotification } from '../../hooks/useNotification.js'
import Janela from '../componentes/Janela'
import VinculoDialog, { ItemVinculo } from '../componentes/VinculoDialog'
import {
  alcanceDaRegra, chaveDaRegra, corpoDoLogin, corpoDoProcessar, faltaNoLogin, FormLogin, LoginCriado, mascaraCpf, Plano, PlanoEquipe, PlanoRegra, PlanoVinculo,
  rotuloTipo, ResultadoProcessar, resumoDoPlano
} from './comum'
import '../componentes/vinculo.scss'
import './solicitacoes.scss'

interface Props {
  /** Id a processar, ou null com a tela fechada. */
  id: number | null
  aoFechar: () => void
  aoProcessar: (resultado: ResultadoProcessar) => void
}

interface VinculoDaGestao { id: number; dashboardId: number; dashboard: string; ambienteId: number; ambiente: string; grupo: string }
interface EquipeDaGestao { id: number; nome: string; membros: number }

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`

/**
 * Processar: a gestão revisa o acesso que o pedido (ou a pessoa de referência) pede, tira ou soma dashboards e equipes,
 * ajusta os valores de regra e aplica tudo de uma vez. Só quando confirma é que o portal grava e a solicitação conclui.
 */
export default function ProcessarSolicitacao({ id, aoFechar, aoProcessar }: Props) {
  const { notify } = useNotification()
  const [plano, setPlano] = useState<Plano | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [vinculos, setVinculos] = useState<PlanoVinculo[]>([])
  const [equipes, setEquipes] = useState<PlanoEquipe[]>([])
  const [regras, setRegras] = useState<PlanoRegra[]>([])
  const [aplicando, setAplicando] = useState(false)
  const [erroAoAplicar, setErroAoAplicar] = useState<string | null>(null)

  const [dialogo, setDialogo] = useState<'vinculos' | 'equipes' | null>(null)
  const [vinculosDaGestao, setVinculosDaGestao] = useState<VinculoDaGestao[] | null>(null)
  const [equipesDaGestao, setEquipesDaGestao] = useState<EquipeDaGestao[] | null>(null)

  const [formLogin, setFormLogin] = useState<FormLogin>({ username: '', empresa: '', tipo: '' })
  const [criandoLogin, setCriandoLogin] = useState(false)
  const [erroLogin, setErroLogin] = useState<string | null>(null)
  const [loginCriado, setLoginCriado] = useState<LoginCriado | null>(null)

  const [novaRegra, setNovaRegra] = useState(false)
  const [formRegra, setFormRegra] = useState({ regraId: '', atributo: '', valor: '', alcance: '' })

  useEffect(() => {
    if (id == null) return
    setPlano(null); setErro(null); setErroAoAplicar(null); setDialogo(null); setNovaRegra(false)
    setVinculosDaGestao(null); setEquipesDaGestao(null); setLoginCriado(null); setErroLogin(null); setCriandoLogin(false)
    let vivo = true
    services.api.solicitacaoPlano(id).then((r: any) => {
      if (!vivo) return
      if (r.fail) { setErro(r.error || 'Não foi possível montar o plano.'); return }
      const p: Plano = r.data
      setPlano(p); setVinculos(p.vinculos); setEquipes(p.equipes); setRegras(p.regras)
      setFormLogin({ username: p.loginParaCriar?.usernameSugerido || '', empresa: '', tipo: '' })
    })
    return () => { vivo = false }
  }, [id])

  // Depois de criar o login, a pessoa leva um tempo para aparecer no portal (a cópia das pessoas é por replicação): confere de novo.
  const recarregarPlano = useCallback(async () => {
    if (id == null) return
    const r = await services.api.solicitacaoPlano(id)
    if (r.fail) return
    const p: Plano = r.data
    setPlano(p)
    // As listas são da gestão: quem estava revisando o plano pode ter tirado ou editado itens enquanto o login era criado.
    // Recarregar só atualiza o que é do servidor (as marcas "Já tem" e "Já está"), nunca devolve o que foi tirado.
    if (p.pessoaId != null) {
      setVinculos(atuais => atuais.map(v => { const n = p.vinculos.find(x => x.vinculoId === v.vinculoId); return n ? { ...v, jaTem: n.jaTem } : v }))
      setEquipes(atuais => atuais.map(e => { const n = p.equipes.find(x => x.id === e.id); return n ? { ...e, jaEsta: n.jaEsta } : e }))
      setRegras(atuais => atuais.map(r => { const n = p.regras.find(x => chaveDaRegra(x) === chaveDaRegra(r)); return n ? { ...r, jaTem: n.jaTem } : r }))
    }
  }, [id])

  const aguardandoPessoa = !!loginCriado && !!plano && plano.pessoaId == null
  useEffect(() => {
    if (!aguardandoPessoa) return
    let tentativas = 0
    const t = setInterval(() => { tentativas++; recarregarPlano(); if (tentativas >= 12) clearInterval(t) }, 5000)
    return () => clearInterval(t)
  }, [aguardandoPessoa, recarregarPlano])

  // As listas completas da Gestão só são buscadas quando a pessoa abre o pop-up de adicionar.
  useEffect(() => {
    if (dialogo === 'vinculos' && !vinculosDaGestao) {
      services.api.novoPortalVinculos().then((r: any) => setVinculosDaGestao(r.fail ? [] : r.data || []))
    }
    if (dialogo === 'equipes' && !equipesDaGestao) {
      services.api.novoPortalEquipes().then((r: any) => setEquipesDaGestao(r.fail ? [] : r.data || []))
    }
  }, [dialogo, vinculosDaGestao, equipesDaGestao])

  const resumo = useMemo(() => resumoDoPlano(vinculos, equipes, regras), [vinculos, equipes, regras])
  const regrasPorNome = useMemo(() => {
    const mapa = new Map<string, PlanoRegra[]>()
    regras.forEach(r => mapa.set(r.regra, [...(mapa.get(r.regra) || []), r]))
    return Array.from(mapa.entries())
  }, [regras])

  if (id == null) return null

  const permissoes = plano?.permissoes
  const semPermissao = !permissoes ? null
    : vinculos.length > 0 && !permissoes.darAcesso ? 'Você não tem permissão para dar acesso a dashboards. Tire os dashboards do plano ou peça a quem tem.'
      : equipes.length > 0 && !permissoes.equipes ? 'Você não tem permissão para colocar pessoas em equipes. Tire as equipes do plano ou peça a quem tem.'
        : regras.length > 0 && !permissoes.regras ? 'Você não tem permissão para ajustar valores de regra. Tire-os do plano ou peça a quem tem.'
          : null
  const semPessoa = !!plano && plano.pessoaId == null
  // Plano só com o que a pessoa já tem ainda pode ser aplicado: nada muda, mas a solicitação conclui.
  const falhaNoLogin = faltaNoLogin(formLogin)
  const podeCriarLogin = !!plano?.loginParaCriar?.configurado && !!permissoes?.criarLogin && !falhaNoLogin && !criandoLogin && !loginCriado
  const podeAplicar = !!plano && !semPessoa && !semPermissao && !resumo.vazio && !aplicando

  const criarLogin = async () => {
    if (!podeCriarLogin) return
    setCriandoLogin(true); setErroLogin(null)
    const r = await services.api.solicitacaoCriarLogin(id, corpoDoLogin(formLogin))
    setCriandoLogin(false)
    if (r.fail) { setErroLogin(r.error || 'Não foi possível criar o login.'); return }
    const criado: LoginCriado = r.data
    setLoginCriado(criado)
    if (criado.apareceNoPortal) await recarregarPlano()
    notify(criado.jaExistia ? `A pessoa já tinha login (usuário ${criado.username}).` : `Login criado: usuário ${criado.username}.`, { variant: 'success' })
  }

  const aplicar = async () => {
    if (!plano || plano.pessoaId == null) return
    setAplicando(true); setErroAoAplicar(null)
    const r = await services.api.solicitacaoProcessar(id, corpoDoProcessar(plano.pessoaId, vinculos, equipes, regras))
    setAplicando(false)
    if (r.fail) { setErroAoAplicar(r.error || 'Não foi possível aplicar o acesso.'); return }
    aoProcessar(r.data)
  }

  const itensDeVinculo: ItemVinculo[] = (vinculosDaGestao || []).map(v => ({
    id: String(v.id), nome: v.dashboard, sub: `${v.ambiente} · ${v.grupo}`, grupo: v.ambiente, busca: `${v.ambiente} ${v.grupo}`,
    ja: vinculos.some(x => x.vinculoId === v.id) ? 'No plano' : undefined
  }))
  const itensDeEquipe: ItemVinculo[] = (equipesDaGestao || []).map(e => ({
    id: String(e.id), nome: e.nome, sub: plural(e.membros, 'pessoa', 'pessoas'), ja: equipes.some(x => x.id === e.id) ? 'No plano' : undefined
  }))

  const regraEscolhida = plano?.regrasDisponiveis.find(r => String(r.id) === formRegra.regraId)
  const regraValida = !!regraEscolhida && formRegra.atributo.trim().length > 0 && formRegra.valor.trim().length > 0

  const adicionarRegra = () => {
    if (!regraEscolhida) return
    const v = vinculos.find(x => String(x.vinculoId) === formRegra.alcance)
    const novo: PlanoRegra = {
      regraId: regraEscolhida.id, regra: regraEscolhida.nome, atributo: formRegra.atributo.trim(), valor: formRegra.valor.trim(),
      dashboardId: v ? v.dashboardId : null, ambienteId: v ? v.ambienteId : null, dashboard: v ? v.dashboard : null, ambiente: v ? v.ambiente : null, jaTem: false
    }
    if (regras.some(r => chaveDaRegra(r) === chaveDaRegra(novo))) { notify('Esse valor já está no plano.', { variant: 'info' }); return }
    setRegras([...regras, novo])
    setNovaRegra(false)
    setFormRegra({ regraId: '', atributo: '', valor: '', alcance: '' })
  }

  const editarValor = (alvo: PlanoRegra, valor: string) =>
    setRegras(regras.map(r => (r === alvo ? { ...r, valor, jaTem: false } : r)))

  return (
    <>
      <div className="nv-veu" onMouseDown={e => { if (e.target === e.currentTarget && !aplicando) aoFechar() }}
           onKeyDown={e => { if (e.key === 'Escape' && !aplicando && !dialogo && !novaRegra) aoFechar() }}>
        <div className="nv sol-detalhe" role="dialog" aria-modal="true" aria-labelledby="pr-titulo">
          <div className="nv-cab">
            <div>
              <h2 id="pr-titulo">Processar solicitação #{id}</h2>
              {plano && <small>{rotuloTipo(plano.tipo)}{plano.pessoaNome ? ` · para ${plano.pessoaNome}` : ''}</small>}
            </div>
            <button type="button" className="nv-ico" onClick={aoFechar} aria-label="Fechar" disabled={aplicando}><CloseIcon /></button>
          </div>

          <div className="nv-corpo sol-corpo">
            {erro && <div className="nv-erro" role="alert">{erro}</div>}
            {!plano && !erro && <p className="ng-explic">Montando o plano…</p>}

            {plano && (<>
              <p className="ng-explic">
                {plano.origem === 'espelho'
                  ? <>O pedido é <b>o mesmo acesso de {plano.espelhoNome || 'outra pessoa'}</b>. O que ela tem hoje já está abaixo.</>
                  : <>O plano parte do que foi pedido.</>}
                {' '}Revise, tire o que não deve ir, some o que falta e ajuste os valores de regra. Nada é gravado até você aplicar.
              </p>

              {plano.aviso && <div className="sol-aviso" role="alert">{plano.aviso}</div>}
              {semPermissao && <div className="sol-aviso" role="alert">{semPermissao}</div>}

              {plano.loginParaCriar && (
                <section className="pr-login">
                  <h3 className="sol-h">Criar o login no AuthManager</h3>
                  <dl className="sol-dados">
                    <dt>Nome</dt><dd>{plano.loginParaCriar.nome}</dd>
                    <dt>E-mail</dt><dd>{plano.loginParaCriar.email}</dd>
                    {plano.loginParaCriar.cpf && <><dt>CPF</dt><dd>{mascaraCpf(plano.loginParaCriar.cpf)}</dd></>}
                    <dt>Perfil</dt><dd>{plano.loginParaCriar.perfil}</dd>
                    <dt>Senha</dt><dd>Senha padrão, com troca obrigatória no primeiro acesso</dd>
                  </dl>
                  {!plano.loginParaCriar.configurado && <small className="sol-ajuda">A criação automática do login ainda não está configurada. Crie o login no AuthManager e volte aqui.</small>}
                  {plano.loginParaCriar.configurado && !permissoes?.criarLogin && <small className="sol-ajuda">Você não tem permissão para criar login. Peça a quem tem, ou crie no AuthManager e volte aqui.</small>}
                  {plano.loginParaCriar.configurado && permissoes?.criarLogin && !loginCriado && (<>
                    <div className="sol-duas">
                      <label className="ng-campo"><span>Usuário</span>
                        <input value={formLogin.username} onChange={e => setFormLogin({ ...formLogin, username: e.target.value })} maxLength={40} aria-label="Usuário" />
                      </label>
                      <label className="ng-campo"><span>Empresa</span>
                        <input value={formLogin.empresa} onChange={e => setFormLogin({ ...formLogin, empresa: e.target.value })} maxLength={100} aria-label="Empresa" />
                      </label>
                    </div>
                    <div className="ng-campo"><span>A pessoa é</span>
                      <div className="sol-vias" role="radiogroup" aria-label="A pessoa é">
                        {(['interno', 'cliente'] as const).map(t => (
                          <button key={t} type="button" role="radio" aria-checked={formLogin.tipo === t} className={`sol-via${formLogin.tipo === t ? ' on' : ''}`}
                                  onClick={() => setFormLogin({ ...formLogin, tipo: t })}>{t === 'interno' ? 'Interna' : 'Cliente'}</button>
                        ))}
                      </div>
                    </div>
                    {erroLogin && <div className="nv-erro" role="alert">{erroLogin}</div>}
                    <div className="sol-atend-botoes">
                      <button type="button" className="ng-btn prim" onClick={criarLogin} disabled={!podeCriarLogin}>{criandoLogin ? 'Criando…' : 'Criar login'}</button>
                      {falhaNoLogin && <small className="sol-ajuda">{falhaNoLogin}</small>}
                    </div>
                  </>)}
                  {loginCriado && (
                    <div className="sol-aviso" role="status">
                      {loginCriado.jaExistia ? 'A pessoa já tinha login' : 'Login criado'} (usuário <b>{loginCriado.username}</b>).
                      {aguardandoPessoa ? ' Aguardando a pessoa aparecer no portal, o que pode levar um pouco…' : ' A pessoa já aparece no portal.'}
                      {aguardandoPessoa && <> <button type="button" className="sol-link" onClick={recarregarPlano}>Verificar de novo</button></>}
                    </div>
                  )}
                </section>
              )}

              <section>
                <div className="pr-cab">
                  <h3 className="sol-h">Dashboards <small>({vinculos.length})</small></h3>
                  <button type="button" className="ng-btn" onClick={() => setDialogo('vinculos')} disabled={!permissoes?.darAcesso || semPessoa || aplicando}
                          title={permissoes?.darAcesso ? undefined : 'Você não tem permissão para dar acesso a dashboards'}>Adicionar dashboards</button>
                </div>
                {vinculos.length === 0 && <p className="ng-explic">Nenhum dashboard no plano.</p>}
                <ul className="pr-lista">
                  {vinculos.map(v => (
                    <li key={v.vinculoId} className="pr-item">
                      <span><b>{v.dashboard}</b><small>{v.ambiente}{v.grupo ? ` · ${v.grupo}` : ''}</small></span>
                      {v.jaTem && <span className="ng-pill">Já tem</span>}
                      <button type="button" className="pr-x" onClick={() => setVinculos(vinculos.filter(x => x !== v))}
                              aria-label={`Tirar ${v.dashboard} de ${v.ambiente}`}><CloseIcon fontSize="inherit" /></button>
                    </li>
                  ))}
                </ul>
              </section>

              <section>
                <div className="pr-cab">
                  <h3 className="sol-h">Equipes <small>({equipes.length})</small></h3>
                  <button type="button" className="ng-btn" onClick={() => setDialogo('equipes')} disabled={!permissoes?.equipes || semPessoa || aplicando}
                          title={permissoes?.equipes ? undefined : 'Você não tem permissão para colocar pessoas em equipes'}>Adicionar equipes</button>
                </div>
                {equipes.length === 0 && <p className="ng-explic">Nenhuma equipe no plano.</p>}
                <ul className="pr-lista">
                  {equipes.map(e => (
                    <li key={e.id} className="pr-item">
                      <span><b>{e.nome}</b></span>
                      {e.jaEsta && <span className="ng-pill">Já está</span>}
                      <button type="button" className="pr-x" onClick={() => setEquipes(equipes.filter(x => x !== e))}
                              aria-label={`Tirar a equipe ${e.nome}`}><CloseIcon fontSize="inherit" /></button>
                    </li>
                  ))}
                </ul>
              </section>

              <section>
                <div className="pr-cab">
                  <h3 className="sol-h">Valores de regra <small>({regras.length})</small></h3>
                  <button type="button" className="ng-btn" onClick={() => setNovaRegra(true)} disabled={!permissoes?.regras || semPessoa || aplicando}
                          title={permissoes?.regras ? undefined : 'Você não tem permissão para ajustar valores de regra'}>Adicionar valor</button>
                </div>
                {regras.length === 0 && <p className="ng-explic">Nenhum valor de regra no plano.</p>}
                {regrasPorNome.map(([nome, itens]) => (
                  <div key={nome} className="pr-regra">
                    <div className="sol-grupo">{nome}</div>
                    <ul className="pr-lista">
                      {itens.map(r => (
                        <li key={chaveDaRegra(r)} className="pr-item">
                          <span className="pr-valor">
                            <small>{r.atributo}</small>
                            <input value={r.valor} onChange={e => editarValor(r, e.target.value)} aria-label={`Valor de ${r.atributo} em ${nome}`} maxLength={500} />
                            <small>{alcanceDaRegra(r)}</small>
                          </span>
                          {r.jaTem && <span className="ng-pill">Já tem</span>}
                          <button type="button" className="pr-x" onClick={() => setRegras(regras.filter(x => x !== r))}
                                  aria-label={`Tirar o valor ${r.valor} de ${nome}`}><CloseIcon fontSize="inherit" /></button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </section>

              {plano.abreVazio.length > 0 && (
                <div className="sol-aviso" role="status">
                  <b>Estes dashboards abririam vazios para {plano.pessoaNome || 'a pessoa'}</b>, porque a regra deles ainda não tem valor para ela:
                  <ul className="pr-vazios">{plano.abreVazio.map(t => <li key={t}>{t}</li>)}</ul>
                  Some um valor de regra acima se ela deve ver dados.
                </div>
              )}
            </>)}

            {erroAoAplicar && <div className="nv-erro" role="alert">{erroAoAplicar}</div>}
          </div>

          <div className="nv-rodape">
            <span className="pr-resumo">
              {plano && !semPessoa && (resumo.vazio ? 'O plano está vazio.' :
                `Vai dar ${plural(resumo.novosAcessos, 'acesso', 'acessos')}${resumo.jaTemAcessos ? ` (${resumo.jaTemAcessos} já existiam)` : ''}, ` +
                `${plural(resumo.novasEquipes, 'entrada em equipe', 'entradas em equipes')} e ${plural(resumo.novasRegras, 'valor de regra', 'valores de regra')}, e concluir.`)}
            </span>
            <button type="button" className="nv-btn" onClick={aoFechar} disabled={aplicando}>Cancelar</button>
            <button type="button" className="nv-btn prim" onClick={aplicar} disabled={!podeAplicar}>{aplicando ? 'Aplicando…' : 'Aplicar e concluir'}</button>
          </div>
        </div>
      </div>

      <VinculoDialog
        aberto={dialogo === 'vinculos'} titulo="Adicionar dashboards ao plano" sub={plano?.pessoaNome ? `Para ${plano.pessoaNome}` : undefined}
        carregando={!vinculosDaGestao}
        abas={[{ id: 'd', rotulo: 'Dashboards', dica: 'Buscar dashboard ou ambiente', unidade: ['dashboard', 'dashboards'], itens: itensDeVinculo }]}
        resumo={(_, total) => total ? <>Soma <b>{plural(total, 'dashboard', 'dashboards')}</b> ao plano. Nada é gravado até você aplicar.</> : <>Cada item é um dashboard num ambiente.</>}
        botao={total => total ? `Somar ${plural(total, 'dashboard', 'dashboards')}` : 'Somar'}
        aoFechar={() => setDialogo(null)}
        aoConfirmar={async sel => {
          const novos = (sel.d || []).map(i => vinculosDaGestao?.find(v => String(v.id) === i.id)).filter((v): v is VinculoDaGestao => !!v)
            .filter(v => !vinculos.some(x => x.vinculoId === v.id))
            .map<PlanoVinculo>(v => ({ vinculoId: v.id, ambienteId: v.ambienteId, ambiente: v.ambiente, grupo: v.grupo, dashboardId: v.dashboardId, dashboard: v.dashboard, jaTem: false }))
          setVinculos([...vinculos, ...novos])
          return null
        }}
      />

      <VinculoDialog
        aberto={dialogo === 'equipes'} titulo="Adicionar equipes ao plano" sub={plano?.pessoaNome ? `Para ${plano.pessoaNome}` : undefined}
        carregando={!equipesDaGestao}
        abas={[{ id: 'e', rotulo: 'Equipes', dica: 'Buscar equipe', unidade: ['equipe', 'equipes'], itens: itensDeEquipe }]}
        resumo={(_, total) => total ? <>Soma <b>{plural(total, 'equipe', 'equipes')}</b> ao plano: quem entra numa equipe vê o que ela vê.</> : <>Quem entra numa equipe vê todos os dashboards dela.</>}
        botao={total => total ? `Somar ${plural(total, 'equipe', 'equipes')}` : 'Somar'}
        aoFechar={() => setDialogo(null)}
        aoConfirmar={async sel => {
          const novas = (sel.e || []).map(i => equipesDaGestao?.find(e => String(e.id) === i.id)).filter((e): e is EquipeDaGestao => !!e)
            .filter(e => !equipes.some(x => x.id === e.id)).map<PlanoEquipe>(e => ({ id: e.id, nome: e.nome, jaEsta: false }))
          setEquipes([...equipes, ...novas])
          return null
        }}
      />

      <Janela aberto={novaRegra} titulo="Adicionar valor de regra" sub={plano?.pessoaNome ? `Para ${plano.pessoaNome}` : undefined} botao="Adicionar ao plano"
              desabilitado={!regraValida} aoConfirmar={adicionarRegra} aoFechar={() => setNovaRegra(false)}>
        <label className="ng-campo"><span>Regra</span>
          <select value={formRegra.regraId} onChange={e => setFormRegra({ ...formRegra, regraId: e.target.value, atributo: '' })} aria-label="Regra">
            <option value="">Escolha…</option>
            {plano?.regrasDisponiveis.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
          </select>
        </label>
        <label className="ng-campo"><span>Atributo</span>
          <input value={formRegra.atributo} onChange={e => setFormRegra({ ...formRegra, atributo: e.target.value })} list="pr-atributos" maxLength={200} aria-label="Atributo" />
          <datalist id="pr-atributos">{(regraEscolhida?.atributos || []).map(a => <option key={a} value={a} />)}</datalist>
        </label>
        <label className="ng-campo"><span>Valor</span>
          <input value={formRegra.valor} onChange={e => setFormRegra({ ...formRegra, valor: e.target.value })} maxLength={500} aria-label="Valor" />
        </label>
        <label className="ng-campo"><span>Onde vale</span>
          <select value={formRegra.alcance} onChange={e => setFormRegra({ ...formRegra, alcance: e.target.value })} aria-label="Onde vale">
            <option value="">Todos os dashboards da regra</option>
            {vinculos.map(v => <option key={v.vinculoId} value={v.vinculoId}>{v.dashboard} · {v.ambiente}</option>)}
          </select>
        </label>
      </Janela>
    </>
  )
}
